import { useState } from "react";
import { companyInfo, contactReasons } from "@/data/mock";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send, MessageSquare, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6 }
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    reason: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Por favor completa los campos requeridos.");
      return;
    }
    // Save to localStorage as mock
    const submissions = JSON.parse(localStorage.getItem("onix_contacts") || "[]");
    submissions.push({ ...formData, date: new Date().toISOString() });
    localStorage.setItem("onix_contacts", JSON.stringify(submissions));
    setSubmitted(true);
    toast.success("Mensaje enviado correctamente.");
  };

  const contactInfo = [
    { icon: Phone, label: "Teléfono", value: companyInfo.phone, href: `tel:${companyInfo.phone}` },
    { icon: Mail, label: "Email", value: companyInfo.email, href: `mailto:${companyInfo.email}` },
    { icon: MapPin, label: "Dirección", value: companyInfo.address, href: null },
    { icon: Clock, label: "Horario", value: "Lun - Vie: 9:00 AM - 6:00 PM", href: null }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-onix-dark">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(6,182,212,0.08),transparent_70%)]" />
          <div className="absolute inset-0" style={{
            backgroundImage: "radial-gradient(rgba(6,182,212,0.05) 1px, transparent 1px)",
            backgroundSize: "50px 50px"
          }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <span className="text-cyan-400 text-sm font-semibold uppercase tracking-widest">Contáctanos</span>
            <h1 className="text-4xl md:text-5xl font-orbitron font-bold text-white mt-4 mb-6">
              Hablemos de tu <span className="text-cyan-400">proyecto</span>
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Estamos listos para ayudarte. Envíanos tu consulta y te responderemos en menos de 24 horas.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <motion.div {...fadeUp} className="lg:col-span-2">
              <h2 className="text-2xl font-orbitron font-bold text-white mb-8">
                Información de <span className="text-cyan-400">contacto</span>
              </h2>
              <div className="space-y-6 mb-10">
                {contactInfo.map((info, i) => (
                  <div key={i} className="flex items-start gap-4 group">
                    <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center shrink-0 group-hover:bg-cyan-500/20 transition-colors">
                      <info.icon size={20} className="text-cyan-400" />
                    </div>
                    <div>
                      <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">{info.label}</div>
                      {info.href ? (
                        <a href={info.href} className="text-white text-sm hover:text-cyan-400 transition-colors">
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-white text-sm">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* WhatsApp CTA */}
              <a
                href={`https://wa.me/525551234567`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-6 py-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/15 hover:border-emerald-500/30 transition-all duration-300"
              >
                <MessageSquare size={20} />
                <div>
                  <div className="text-sm font-medium">Escríbenos por WhatsApp</div>
                  <div className="text-xs text-emerald-500/70">Respuesta inmediata</div>
                </div>
              </a>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-3"
            >
              {submitted ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center p-10">
                    <div className="w-20 h-20 rounded-full bg-cyan-500/10 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle size={40} className="text-cyan-400" />
                    </div>
                    <h3 className="text-2xl font-orbitron font-bold text-white mb-3">
                      ¡Mensaje Enviado!
                    </h3>
                    <p className="text-slate-400 mb-8">
                      Gracias por contactarnos. Nuestro equipo se pondrá en contacto contigo en menos de 24 horas.
                    </p>
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ name: "", email: "", phone: "", reason: "", message: "" });
                      }}
                      className="px-6 py-3 border border-cyan-500/30 text-cyan-400 rounded-xl hover:bg-cyan-500/10 transition-all duration-300 text-sm font-medium"
                    >
                      Enviar otro mensaje
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="p-8 rounded-2xl bg-onix-card border border-white/5">
                  <div className="grid md:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Nombre completo *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                        placeholder="Tu nombre"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                        placeholder="correo@ejemplo.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Teléfono</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Motivo de consulta</label>
                      <select
                        name="reason"
                        value={formData.reason}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10 text-white text-sm focus:outline-none focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer"
                      >
                        <option value="" className="bg-onix-dark">Selecciona una opción</option>
                        {contactReasons.map((reason, i) => (
                          <option key={i} value={reason} className="bg-onix-dark">
                            {reason}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm text-slate-400 mb-2">Mensaje *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="5"
                      className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20 transition-all resize-none"
                      placeholder="Cuéntanos sobre tu proyecto..."
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="group w-full flex items-center justify-center gap-2 px-8 py-4 bg-cyan-500 text-onix-dark font-semibold rounded-xl hover:bg-cyan-400 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/25"
                  >
                    <Send size={18} />
                    Enviar Mensaje
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
